import * as React from 'react';
import { ChangeEvent, useEffect, useState } from 'react';
import { FormControl, Image, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ITotalPlatforms } from '../../../../../../../../model/platform/ITotalPlatforms';
import Counter from '../../../../../../../../shared/components/Counter';
import { CustomImageHandler } from '../../../../../../../../shared/components/CustomImageHandler';
import {
    calculateAvgReach,
    checkNumberPosts,
    checkPrice,
    getCpmValue,
    getOldPricePerPlatform,
    getPostsPerPlatform,
    getPriceOneContent,
    getPricePerPlatform,
} from '../../../../../../../../shared/functions/Functions';
import { TrashIcon } from '../../../../../../../../shared/Icons';
import { setContractItems } from '../../../../../../../../store/brand/contractBrandSlice';
import { IAllStates } from '../../../../../../../../store/rootReducer';
import { numberFormatter } from '../../../../../../../../utils/helpers/numberFormater';
import { priceSplitter } from '../../../../../../../../utils/helpers/priceSplitter';
import Flag from '../../../../../../../../shared/components/flag/Flag';

interface IPlatformAccordionProps {
    platform: ITotalPlatforms;
}

const PlatformAccordion = ({ platform }: IPlatformAccordionProps) => {
    const { initialContract, contractModalStatus, isCharity, isAnnexMode } = useSelector(
        (state: IAllStates) => state.contractBrand
    );
    const [expended, setExpended] = useState(false);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    useEffect(() => {
        setExpended(contractModalStatus === 'negotiate');
    }, [contractModalStatus]);

    function deleteOfferItem(socialPlatformId: number) {
        const initialOfferItemCopy = [...initialContract.contractItems];
        const selectedOfferItem = initialOfferItemCopy.find(item => item.socialPlatformId === socialPlatformId);
        const index = selectedOfferItem ? initialOfferItemCopy.indexOf(selectedOfferItem) : 0;
        initialOfferItemCopy[index] = { ...initialOfferItemCopy[index], nbPosts: 0, price: 0 };
        dispatch(setContractItems(initialOfferItemCopy));
    }

    function changeHandlerOfferItem(
        socialPlatformId: number,
        event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        nbPosts?: number,
        mainPlatformId?: number
    ) {
        const value = event && Number(event.target.value);
        const initialOfferItemCopy = [...initialContract.contractItems];
        const selectedOfferItem = initialOfferItemCopy.find(item => item.socialPlatformId === socialPlatformId);
        const index = selectedOfferItem ? initialOfferItemCopy.indexOf(selectedOfferItem) : 0;
        if (selectedOfferItem) {
            if (nbPosts === 0) {
                deleteOfferItem(socialPlatformId);
            } else {
                initialOfferItemCopy[index] = {
                    ...initialOfferItemCopy[index],
                    price: value === undefined ? initialOfferItemCopy[index].price : value,
                    nbPosts: nbPosts === undefined ? initialOfferItemCopy[index].nbPosts : nbPosts,
                };
                dispatch(setContractItems(initialOfferItemCopy));
            }
        }
    }

    const isStrikeTrough = getPostsPerPlatform(initialContract.contractItems, platform.id) === 0;

    return (
        <>
            <tr className="cursor-pointer" onClick={() => setExpended(!expended)}>
                <td>
                    <div className="d-flex align-items-center">
                        <CustomImageHandler altTag={platform?.name ?? ''} photoPath={platform?.path ?? null} />
                        <span
                            className={`text-primary font-weight-bold pl-1 mb-0 ${
                                isStrikeTrough ? 'text-line-through' : ''
                            }`}
                        >
                            x
                            {contractModalStatus === 'preview'
                                ? platform.nbPostsTotal
                                : getPostsPerPlatform(initialContract.contractItems, platform.id)}
                        </span>
                        {expended ? (
                            <Image className="ml-2" alt="image-down" src="/icons/img_arrow_up.svg" />
                        ) : (
                            <Image className="ml-2" alt="image-down" src="/icons/img_arrow_down.svg" />
                        )}
                    </div>
                </td>
                <td className="text-center">
                    <p className={isStrikeTrough ? 'text-line-through' : ''}>
                        {numberFormatter(platform.totalFollowers)}
                    </p>
                </td>
                <td className="text-center">
                    <p className={isStrikeTrough ? 'text-line-through' : ''}>
                        {numberFormatter(+calculateAvgReach(initialContract?.contractItems, platform)?.toFixed(2))}
                    </p>
                </td>
                <td>
                    <div className={`d-flex justify-content-center ${isStrikeTrough ? 'text-line-through' : ''}`}>
                        {platform?.audiences.map(country => (
                            <div key={country.id} className="d-flex pl-2 align-items-center">
                                <Flag className="rounded" code={country?.metric} width={26} />
                                <div className="d-flex flex-column pl-2">
                                    <p className="text-primary font-weight-bold mb-0">{Math.round(country.value)}%</p>
                                    <p className="text-secondary mb-0">{numberFormatter(country.followers)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </td>
                <td className={`${isCharity ? 'd-none' : 'text-center'} ${isStrikeTrough ? 'text-line-through' : ''}`}>
                    <p className="euro-symbol">{getCpmValue(initialContract.contractItems, platform)}</p>
                </td>
                <td className={`${isCharity ? 'd-none' : 'text-end'} ${isStrikeTrough ? 'text-line-through' : ''}`}>
                    <div className="d-flex align-items-center justify-content-end">
                        {getOldPricePerPlatform(initialContract.contractItems, platform.id) > 0 && (
                            <p className="euro-symbol text-line-through mr-2">
                                {priceSplitter(
                                    Math.round(
                                        Math.round(getOldPricePerPlatform(initialContract.contractItems, platform.id))
                                    )
                                )}
                            </p>
                        )}
                        <p className="euro-symbol">
                            {priceSplitter(Math.round(getPricePerPlatform(initialContract.contractItems, platform.id)))}
                        </p>
                    </div>
                </td>
            </tr>
            {platform?.contractItems?.map(item => {
                if (!item || !item.socialPlatform || !item.socialPlatform.id) {
                    return;
                }

                const defaultPosts = item?.nbPosts ?? 0;
                const price = checkPrice(item.socialPlatform.id, item.price, initialContract?.contractItems ?? []);

                const postNumber = checkNumberPosts(
                    item.socialPlatform.id,
                    defaultPosts,
                    initialContract?.contractItems ?? []
                );

                return (
                    <tr
                        key={item.id}
                        className={`position-relative
                        ${
                            postNumber === 0 || (!item.id && contractModalStatus === 'preview')
                                ? 'text-line-through'
                                : ''
                        }
                        ${expended ? '' : 'd-none'}
                     `}
                    >
                        <td colSpan={2}>
                            <div className="d-flex align-items-center pl-2">
                                {contractModalStatus === 'negotiate' && !(isAnnexMode && !!item.postingsUsed) && (
                                    <span className="mb-0" onClick={() => deleteOfferItem(item.socialPlatform.id)}>
                                        <TrashIcon width="15" height="16" classes="cursor-pointer mr-2" />
                                    </span>
                                )}
                                <CustomImageHandler
                                    altTag={item?.socialPlatform?.name ?? ''}
                                    photoPath={item?.socialPlatform?.path ?? ''}
                                />
                                <span className="px-2 mb-0">
                                    {t(`general.platforms.${item?.socialPlatform?.name}`)}
                                </span>
                            </div>
                        </td>
                        <td className="mb-0 delete-margin-bottom">
                            <div className="mr-3">
                                {contractModalStatus === 'negotiate' ? (
                                    <Counter
                                        defaultCount={postNumber}
                                        setNumberItems={changeHandlerOfferItem}
                                        id={item.socialPlatform.id}
                                        itemId={item?.socialPlatform?.id}
                                        minCount={isAnnexMode ? item.postingsUsed ?? 0 : 0}
                                        minHint={
                                            isAnnexMode && !!item.postingsUsed
                                                ? t('brand.campaign.book.contract.modal.annex.min_posts', {
                                                      count: item.postingsUsed,
                                                  })
                                                : undefined
                                        }
                                    />
                                ) : (
                                    <span className="text-muted font-weight-bold mb-0 ">x {item?.nbPosts}</span>
                                )}
                            </div>
                        </td>
                        <td className={`${isCharity ? 'd-none' : ''}`}>
                            <div className="text-muted text-center">
                                <p>{t('influencer.offer_management.modal.price_of_one_content')}</p>
                                <div className="euro-symbol">
                                    {getPriceOneContent(initialContract.contractItems, item.socialPlatform.id)}
                                </div>
                            </div>
                        </td>
                        <td className={`${isCharity ? 'd-none' : ''}`}></td>
                        <td className={`${isCharity ? 'd-none' : 'd-flex justify-content-end'}`}>
                            {contractModalStatus === 'negotiate' ? (
                                <InputGroup id="price-input">
                                    <InputGroup.Text>€</InputGroup.Text>
                                    <FormControl
                                        min={0}
                                        onChange={(event: any) =>
                                            changeHandlerOfferItem(
                                                item.socialPlatform.id,
                                                event,
                                                undefined,
                                                platform.id
                                            )
                                        }
                                        type="number"
                                        value={price === 0 ? '' : price}
                                        disabled={postNumber < 1}
                                        placeholder="0"
                                        required={isCharity ? false : postNumber > 0}
                                        name="price"
                                        id="price-input"
                                        onKeyPress={(event: any) =>
                                            (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                        }
                                    />
                                </InputGroup>
                            ) : (
                                <p className="text-muted mb-0 mt-2 euro-symbol">
                                    {priceSplitter(Math.round(item?.price ?? 0))}
                                </p>
                            )}
                        </td>
                    </tr>
                );
            })}
        </>
    );
};

export default PlatformAccordion;
